/**
 * @fileoverview Input component - Atomic UI element
 * 
 * A flexible, accessible input component with variants and error states.
 * Built with Tailwind CSS and CVA for variant management.
 * 
 * @module ui/shared/atoms/Input
 */

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../utils/cn";

/**
 * Input variant definitions using CVA
 */
// cva takes one base and one config. Passing several of either, as this used to,
// drops everything after the second argument, so the variants never applied.
const inputVariants = cva(
  [
    "flex w-full rounded-md bg-white px-3 py-2 text-sm",
    "file:border-0 file:bg-transparent file:text-sm file:font-medium",
    "placeholder:text-gray-400",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
    "disabled:cursor-not-allowed disabled:opacity-50",
  ],
  {
    variants: {
      hasBorder: {
        true: "border",
        false: "border-0 shadow-none",
      },
      variant: {
        default: "border-gray-300 focus-visible:ring-primary-500",
        error: "border-red-500 focus-visible:ring-red-500",
        success: "border-green-500 focus-visible:ring-green-500",
      },
      size: {
        default: "h-10",
        sm: "h-9 text-sm",
        lg: "h-11 text-base",
      },
    },
    defaultVariants: {
      hasBorder: true,
      variant: "default",
      size: "default",
    },
  }
);

/**
 * Input component props
 */
export interface InputProps
  // `size` is omitted because the variant scale owns it; the HTML attribute is a
  // character count, which is not what any call site means here.
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {
  /**
   * Error message to display
   */
  error?: string;
  /**
   * Helper text to display
   */
  helperText?: string;
  /**
   * Show border (default: true)
   */
  hasBorder?: boolean;
}

/**
 * Input component
 * 
 * A flexible input component with error states and helper text.
 * Fully accessible with proper ARIA attributes.
 * 
 * @param {InputProps} props - Input props
 * @returns {JSX.Element} Input element with optional error/helper text
 * 
 * @example
 * <Input placeholder="Enter your name" />
 * 
 * @example
 * <Input variant="error" error="This field is required" />
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, variant, size, error, helperText, hasBorder, ...props }, ref) => {
    const inputVariant = error ? "error" : variant;
    
    return (
      <div className="w-full">
        <input
          className={cn(inputVariants({ variant: inputVariant, size, hasBorder, className }))}
          ref={ref}
          aria-invalid={error ? "true" : undefined}
          aria-describedby={error || helperText ? `${props.id || "input"}-helper` : undefined}
          {...props}
        />
        {(error || helperText) && (
          <p
            id={`${props.id || "input"}-helper`}
            className={cn(
              "mt-1 text-sm",
              error ? "text-red-600" : "text-gray-500"
            )}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export { Input, inputVariants };

